import React, { useEffect, useRef, useState } from 'react'
import { get, size, isEmpty } from 'lodash'
import { useModal } from '@ggs/ui/common'
import { Box, Link } from '@mui/material'
import { CourseSectionsList } from '../../'
import { useAcademicApi, useAcademicExpApi, useAcademicDataDigest } from '../../../local'
import { Typography } from '@mui/material'
import { useDispatch, addNotification, useSelector } from '@ggs/store'
import { WeeklyTimeTable } from '../../calendar'
import { Loader } from '../../loader'
import { useI18n } from '@ggs/i18n'

import './ProgramStudyView.scss'

/**
 * @typedef {import('../../../definitions').CourseSectionExpRecord} CourseSectionExpRecord
 * @typedef {import('../../../definitions').StandardTimetableRecord} StandardTimetableRecord
 */

/**
 * @typedef {Object} ProgramStudyViewProps
 */

/**
 * @typedef {Object} TimetableModal
 * @property {string} title
 * @property {JSX.Element} content
 */

/**
 * Program of study section list.
 * @return {JSX.Element}
 */
const ProgramStudyView = () => {
  const { t } = useI18n()
  const { getStandardTimetables, getStandardTimetable } = useAcademicApi()
  const { getCourseSections } = useAcademicExpApi()
  const { digestCourseSectionsTimetable } = useAcademicDataDigest()
  const [dataTable, setDataTable] = useState(null)
  const dispatch = useDispatch()
  const standardTimetableId = get(
    window,
    'drupalSettings.react.programStudyId',
    '7f0e5533-9305-4414-b0cb-04fcdffa0d3a'
  )
  const academicPeriod = useSelector((state) => state.settings.academicPeriod)
  const pageSize = 500

  /**
   * @type {StandardTimetableRecord}
   */
  const defaultProgramStudy = null
  const [standardTimetable, setStandardTimetable] = useState(defaultProgramStudy)

  /**
   * @type {CourseSectionExpRecord[]}
   */
  const defaultCourseSections = null
  const [courseSections, setCourseSections] = useState(defaultCourseSections)
  const [loading, setLoading] = useState(true)

  /**
   * @type {TimetableModal}
   */
  const defaultModalData = {
    title: '',
    content: null,
  }
  const modalData = useRef(defaultModalData)

  const { modal, triggerModal } = useModal({
    scroll: 'body',
    title: () => (
      <Box className="modal-title">
        <h2>{modalData.current.title}</h2>
      </Box>
    ),
    content: () => {
      return <Box>{modalData.current.content}</Box>
    },
  })

  /**
   * Open timetable modal.
   * @param {TimetableModal} tableColumn
   */
  const openModal = (tableColumn) => {
    modalData.current = {
      title: tableColumn.title,
      content: tableColumn.content,
    }

    triggerModal()
  }

  const resolveProgramStudyData = async () => {
    setLoading(true)
    const standardTimetable = await getStandardTimetable(standardTimetableId)

    if (standardTimetable) {
      const courseSectionsIds = standardTimetable.courseSectionIds || null
      if (!courseSectionsIds) {
        setCourseSections([])
        setLoading(false)
      }

      const courseSectionsResponse = await getCourseSections({
        pageSize,
        courseSectionId: courseSectionsIds,
        academicPeriodId: [academicPeriod],
      })

      if (size(courseSectionsResponse.pageItems) > 0) {
        setCourseSections(courseSectionsResponse.pageItems)
      }

      setStandardTimetable(standardTimetable)
    }

    setLoading(false)
  }

  /**
   * Resolve program of studies course section data.
   */
  const resolveProgramStudySectionsData = async () => {
    setLoading(true)
    const standardTimetable = await getStandardTimetable(standardTimetableId)
    if (!standardTimetable) {
      dispatch(
        addNotification(
          'not_found_stt_error',
          'error',
          t('Standard timetable not found for {{id}}', { id: standardTimetableId })
        )
      )

      return
    }

    const courseSectionIds = standardTimetable.courseSectionIds || null

    const courseSectionsResult = await getCourseSections({
      pageSize,
      courseSectionId: courseSectionIds,
    })

    const courseSections = courseSectionsResult?.pageItems || null
    const timeTableData = digestCourseSectionsTimetable(courseSections, standardTimetable)

    // console.log(timeTableData)
    setDataTable(timeTableData)
    setLoading(false)
  }

  useEffect(() => {
    resolveProgramStudyData().then(() =>
      console.log('Resolved program of study record from Academic API.')
    )
  }, [academicPeriod])

  useEffect(() => {
    resolveProgramStudySectionsData().then(() =>
      console.log('Resolving program of studies timetables data')
    )
  }, [])

  if (loading || !standardTimetable) {
    return <Loader />
  }

  /**
   * Custom render function for timetable calendar modal link.
   * @param {number} dataIndex
   * @return {JSX.Element}
   */
  const renderTimetableLink = (dataIndex) => {
    if (!dataTable || !dataTable[dataIndex]) {
      return null
    }

    const meetings = dataTable[dataIndex].meetings
    const timetableModal = {
      title: dataTable[dataIndex].name,
      content: <WeeklyTimeTable timeTableList={meetings} />,
    }

    return (
      <>
        {!isEmpty(meetings) ? (
          <Link
            onClick={(e) => {
              e.preventDefault()
              openModal(timetableModal)
            }}
            href="#"
          >
            {t('View Timetable')}
          </Link>
        ) : (
          t('No timetable available')
        )}
      </>
    )
  }

  return (
    <div className="program-study-view">
      <div className="program-study-view__description">
        <p>{standardTimetable.description}</p>
      </div>
      {courseSections ? (
        <div className="program-study-view__tables">
          <CourseSectionsList
            courseSections={courseSections}
            sectionViewBasePath={`/browse-timetables/course-section/${standardTimetableId}`}
          />
        </div>
      ) : (
        <div className="course-view__tables">
          <Typography>
            {t('Course sections not found for selected program of study standard timetable.')}
          </Typography>
        </div>
      )}
    </div>
  )
}

export default ProgramStudyView
